import React from 'react';
import { colors } from '@styleseat/brand';
import { Box } from '@styleseat/ui';
import { ProSettingsLayout } from '../../settings/ProSettingsLayout';
import { ArrowsArrowLeftIcon } from '../../../ui/Icons';
import TransitionSpinner from '../../../ui/TransitionSpinner';
import { ContactListContainer } from '../ContactList';
import { PrivilegedProvider } from '../../../../api/Providers';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { ArrowsRotateCwIcon } from '../../../ui/Icons/ArrowsRotateCwIcon';
import { Stack } from '../../../ui';
import { useDebouncedState } from '../../../../hooks';
import { ContactListHeader } from '../ContactList/ContactListHeader';
import { EmptyClientListContainer } from '../EmptyClientList/EmptyClientListContainer';

type ProClientSelectProps = {
  onClose: () => void;
  onClientReload: () => void;
  loading: boolean;
  provider: Pick<PrivilegedProvider, 'id' | 'profile_phase_one_complete_date' | 'creation_time'>;
  contacts: IProviderClient[];
  onContactSelect: (client: IProviderClient) => void;
  onNewClientSaved: (client: IProviderClient) => void;
  isContactHidden?: (client: IProviderClient) => boolean;
  isContactDisabled?: (client: IProviderClient) => boolean;
  onAddClientClick: () => void;
  highlightSelectedContact?: boolean;
  hideAddClientButton?: boolean;
  showAddIncentive?: boolean;
};

export const ProClientSelect: React.FC<ProClientSelectProps> = ({
  onClose,
  onClientReload,
  loading,
  provider,
  contacts,
  onContactSelect,
  onNewClientSaved,
  isContactHidden,
  isContactDisabled,
  onAddClientClick,
  highlightSelectedContact = true,
  hideAddClientButton = false,
  showAddIncentive,
}) => {
  const {
    immediateValue: searchText,
    delayedValue: filterText,
    setValue: onSearchChange,
  } = useDebouncedState<string>('');

  return (
    <ProSettingsLayout
      title="Select Client"
      leftButtonProps={{
        children: (
          <Box alignItems="center">
            <ArrowsArrowLeftIcon color={colors.ash} width={20} height={20} />
          </Box>
        ),
        onClick: onClose,
        testID: 'ProClientSelect-button--cancel',
      }}
      rightButtonProps={{
        children: (
          <Box alignItems="center">
            <ArrowsRotateCwIcon color={colors.punch} width={20} height={20} />
          </Box>
        ),
        onClick: onClientReload,
        testID: 'ProClientSelect-button--refresh',
      }}
      testID="ProClientSelect"
    >
      {/* No ModalBox needed here because contact list is a virtualized, scrollable list */}
      {loading ? <TransitionSpinner /> : (
        <Stack flex={1}>
          <ContactListHeader
            searchText={searchText}
            showAddButton={!!contacts?.length && !hideAddClientButton}
            provider={provider}
            onAddClientClick={onAddClientClick}
            onSearchChange={onSearchChange}
            showEmptyState={!(contacts?.length >= 10)}
            topBarSearchBox={!!contacts?.length}
            showAddIncentive={showAddIncentive}
          />
          {!contacts?.length ? (
            <EmptyClientListContainer
              providerId={provider?.id}
              onNewClientSaved={onNewClientSaved}
            />
          ) : (
            <ContactListContainer
              filterText={filterText}
              contacts={contacts}
              provider={provider}
              onContactSelect={onContactSelect}
              isContactHidden={isContactHidden}
              isContactDisabled={isContactDisabled}
              highlightSelectedContact={highlightSelectedContact}
              hideEmptyState={contacts?.length >= 10}
              onNewClientSaved={onNewClientSaved}
            />
          )}
        </Stack>
      )}
    </ProSettingsLayout>
  );
};
